import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Select } from '@/shared/components/ui/Select';
import { Input } from '@/shared/components/ui/Input';
import { useLeadMutations } from '../hooks/useLeads';
import { CALL_DISPOSITIONS } from '../constants';

interface CallLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId: string | null;
  leadName: string;
}

export function CallLogModal({ isOpen, onClose, leadId, leadName }: CallLogModalProps) {
  const [disposition, setDisposition] = useState('talked');
  const [durationSec, setDurationSec] = useState('');
  const [notes, setNotes] = useState('');
  const { logCall } = useLeadMutations();

  const reset = () => {
    setDisposition('talked');
    setDurationSec('');
    setNotes('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    logCall.mutate(
      { id: leadId, payload: { disposition, duration_sec: Number(durationSec) || 0, notes: notes.trim() } },
      {
        onSuccess: () => {
          toast.success('Call logged successfully');
          onClose();
          reset();
        },
        onError: (err: any) => toast.error(err.response?.data?.error || err.message || 'Failed to log call'),
      },
    );
  };

  return (
    <Modal open={isOpen} onClose={onClose} title={`Log Call · ${leadName}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Call Outcome"
          options={CALL_DISPOSITIONS.map((d) => ({ value: d.value, label: d.label }))}
          value={disposition}
          onChange={(e) => setDisposition(e.target.value)}
        />
        <Input
          label="Duration (seconds)"
          type="number"
          min="0"
          value={durationSec}
          onChange={(e) => setDurationSec(e.target.value)}
          placeholder="e.g. 120"
        />
        <div>
          <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Notes</label>
          <textarea
            className="w-full rounded-card-sm border border-outline bg-surface px-3 py-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Summary of the call…"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={logCall.isPending}>
            Save Call Log
          </Button>
        </div>
      </form>
    </Modal>
  );
}
